import { Box, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import React from 'react'

import { userReadonlyAtom } from '../../domains/user/AtomUsers'
import { UserAvatar } from '../../utils/gravatar'

/**
 * MessageBubble - Displays a single chat message
 *
 * @param {Object} message - Message object containing text, sender, timestamp
 * @param {boolean} isCurrentUser - Whether the message is from the current user
 * @param {Object} sx - Additional styles to apply
 */
export default function MessageBubble({ message, isCurrentUser = false, sx = {} }) {
  const { text, sender, timestamp } = message

  const user = useAtomValue(userReadonlyAtom(sender?.resourceId))

  return (
    <Stack
      direction={isCurrentUser ? 'row-reverse' : 'row'}
      spacing={1}
      alignItems="flex-start"
      sx={{
        mb: 2,
        maxWidth: '100%',
        ...sx,
      }}
    >
      <UserAvatar user={user} size="small" />

      <Box
        sx={{
          maxWidth: { xs: '75%', sm: '60%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
      >
        {!isCurrentUser && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mb: 0.5,
              px: 1,
            }}
          >
            {sender?.name || 'Unknown'}
          </Typography>
        )}

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: isCurrentUser ? 'primary.main' : 'background.paper',
            color: isCurrentUser ? 'primary.contrastText' : 'text.primary',
            border: isCurrentUser ? 'none' : '1px solid',
            borderColor: 'divider',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Typography variant="body1">{text}</Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
            px: 1,
          }}
        >
          {dayjs(timestamp).format('HH:mm')}
        </Typography>
      </Box>
    </Stack>
  )
}
